import React, { useMemo } from "react";

import isEmpty from "lodash/isEmpty";
import { matchPath, useHistory } from "react-router-dom";

import useGetFeedsSummaryV2 from "@fartherfinance/frontend/api/Notifications/hooks/useGetFeedsSummaryV2";
import useGetFeedsV2 from "@fartherfinance/frontend/api/Notifications/hooks/useGetFeedsV2";
import useUpdateFeedResource from "@fartherfinance/frontend/api/Notifications/hooks/useUpdateFeedResource";
import useUpdateFeeds from "@fartherfinance/frontend/api/Notifications/hooks/useUpdateFeeds";
import { GetFeedsQueryParamsV2 } from "@fartherfinance/frontend/api/Notifications/requests/getFeeds.v2";
import {
  FeedStatus,
  FeedV2,
} from "@fartherfinance/frontend/api/Notifications/Types";
import { AdvisorId, FeedId } from "@fartherfinance/frontend/api/Types";

import { NotificationsPath } from "@src/config/routing/RouterPaths";
import { usePersistedQueryParams } from "@src/multiCustodian/hooks/Advisor/usePersistedQueryParams";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";

import AdvisorContainer from "./AdvisorContainer";
import { FEEDS_DEFAULT_QUERY_PARAMS } from "./Notifications/common";
import NotificationPreview from "./Notifications/NotificationPreview/NotificationPreview";
import NotificationsList from "./Notifications/NotificationsList/NotificationsList";
import { resolveRedirectionUrl } from "./Notifications.utils";

import styles from "./Notifications.module.css";

interface NotificationsProps {
  personId: AdvisorId;
}

const Notifications: React.FC<React.PropsWithChildren<NotificationsProps>> = ({
  children,
  personId,
}) => {
  const [queryParams, setQueryParams] =
    usePersistedQueryParams<GetFeedsQueryParamsV2>(FEEDS_DEFAULT_QUERY_PARAMS);

  const auth = useAdvisorRequestAuth();
  const statusNotification = useStatusNotification();
  const history = useHistory();

  const authOrNull = isEmpty(queryParams) ? null : auth;
  const {
    data: feedsData,
    isLoading,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetFeedsV2(personId, authOrNull, queryParams);
  const { data: feedsSummary } = useGetFeedsSummaryV2(personId, auth);
  const callUpdateFeedResource = useUpdateFeedResource(auth);
  const callUpdateFeeds = useUpdateFeeds(auth);

  const feeds = useMemo(() => {
    return feedsData?.pages.flatMap((page) => page?.values ?? []) ?? [];
  }, [feedsData]);

  const isFeedsListEmpty = feeds.length === 0;

  const matchedPath = matchPath<Record<"notificationId", FeedId>>(
    location.pathname,
    {
      path: `/Advisor/${NotificationsPath}/:notificationId`,
      exact: true,
    }
  );
  const selectedNotificationId = matchedPath?.params.notificationId;
  const selectedNotification = useMemo(() => {
    return feeds.find((feed) => feed.feedId === selectedNotificationId);
  }, [feeds, selectedNotificationId]);

  const handleChangeQueryParams = (
    newQueryParams: GetFeedsQueryParamsV2
  ): void => {
    setQueryParams({ ...queryParams, ...newQueryParams });
  };

  const handleMarkAllNotificationsAsRead = async (): Promise<void> => {
    try {
      await callUpdateFeeds({
        personId,
        request: {
          updateStatusFrom: FeedStatus.Values.UNREAD,
          updateStatusTo: FeedStatus.Values.READ,
        },
      });
    } catch (_error) {
      statusNotification(
        "Failed to mark all feed notifications as read.",
        "Error"
      );
    }
  };

  const handleToggleNotificationStatus = async (
    feed: FeedV2,
    toggledStatus: FeedStatus
  ): Promise<void> => {
    try {
      await callUpdateFeedResource({
        feedId: feed.feedId,
        resourceId: feed.resourceId,
        resourceType: feed.resourceType,
        personId,
        request: { status: toggledStatus },
      });
    } catch (_error) {
      statusNotification("Failed to update feed notification.", "Error");
    }
  };

  const handleClickNotification = async (feed: FeedV2): Promise<void> => {
    const redirection = resolveRedirectionUrl(feed);

    if (redirection) {
      handleToggleNotificationStatus(feed, FeedStatus.Values.READ);
      if (redirection.isExternal) {
        window.open(redirection.url, "_blank");
      } else {
        history.push({
          pathname: redirection.url,
          search: history.location.search,
        });
      }
    }
  };

  return (
    <AdvisorContainer>
      <div className={styles.container}>
        <div className={styles.list}>
          <NotificationsList
            feeds={feeds}
            isEmpty={isFeedsListEmpty}
            isLoading={isLoading}
            isLoadingMore={isFetchingNextPage}
            hasMore={Boolean(hasNextPage)}
            selectedNotificationId={selectedNotificationId}
            queryParams={queryParams}
            // NOTE: react-query is very specific about not requesting next page while there is an active refetch
            // as in case of infinite queries this might produce duplicate data
            onLoadMore={() => !isFetching && fetchNextPage()}
            onMarkAllAsRead={handleMarkAllNotificationsAsRead}
            onNotificationStatusToggle={handleToggleNotificationStatus}
            onNotificationClick={handleClickNotification}
            onQueryParamChange={handleChangeQueryParams}
          />
        </div>

        <div className={styles.preview}>
          <NotificationPreview
            selectedNotification={selectedNotification}
            unreadCount={feedsSummary?.unreadCount}
          >
            {children}
          </NotificationPreview>
        </div>
      </div>
    </AdvisorContainer>
  );
};

export default Notifications;
